/**
 * „KI-Entwurf" am Ticket (Spec 9.2).
 *
 * Drei Zustaende, alle sichtbar: **wird erzeugt** (Zeile ohne `generated_at`),
 * **fehlgeschlagen** (`error`) und **fertig**. Unter dem Entwurf stehen die
 * Quellen, aus denen er gebaut wurde.
 *
 * **Die Schaltflaeche heisst „Übernehmen", nicht „Senden".** Sie laedt den
 * Text in den Antwort-Editor; abgeschickt wird er dort — mit demselben Klick,
 * den auch eine selbst getippte Antwort braucht (Architektur-Invariante 3).
 * Dieses Panel kennt keinen Sende-Aufruf.
 */
import { renderTiptapText, type AiCitation, type AiDraft } from '@tickethub/shared';
import { Bot, FileText, Ticket as TicketIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

type AiDraftPanelProps = {
  draft: AiDraft | null;
  /** `true`, solange die Anfrage laeuft bzw. der Job noch arbeitet. */
  busy: boolean;
  disabled: boolean;
  onRequest: () => void;
  onTake: (draft: AiDraft) => void;
  onDiscard: (draft: AiDraft) => void;
};

function CitationItem({ citation }: { citation: AiCitation }) {
  const Icon = citation.kind === 'kb_article' ? FileText : TicketIcon;
  return (
    <li className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon aria-hidden="true" className="size-3 shrink-0" />
      <span className="truncate">
        {citation.reference ? `${citation.reference} — ` : ''}
        {citation.title}
      </span>
      {citation.score !== null ? (
        <Badge variant="outline" className="ml-auto shrink-0">
          {citation.score.toFixed(2)}
        </Badge>
      ) : null}
    </li>
  );
}

export function AiDraftPanel({
  draft,
  busy,
  disabled,
  onRequest,
  onTake,
  onDiscard,
}: AiDraftPanelProps) {
  const { t } = useTranslation();

  const pending = busy || (draft !== null && draft.generated_at === null && draft.error === null);
  const text = draft ? renderTiptapText(draft.draft_json).trim() : '';
  const ready = draft !== null && draft.generated_at !== null && draft.status !== 'discarded';

  return (
    <section className="space-y-3 rounded-lg border p-4" aria-label={t('ai.draft_title')}>
      <div className="flex flex-wrap items-center gap-2">
        <Bot aria-hidden="true" className="size-4" />
        <h2 className="text-sm font-medium">{t('ai.draft_title')}</h2>
        {draft && draft.status !== 'proposed' ? (
          <Badge variant="secondary">{t(`ai.draft_status_${draft.status}`)}</Badge>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          disabled={disabled || pending}
          onClick={onRequest}
        >
          {pending ? t('ai.draft_generating') : t('ai.draft_request')}
        </Button>
      </div>

      {pending ? <p className="text-sm text-muted-foreground">{t('ai.draft_generating_hint')}</p> : null}

      {draft?.error ? (
        <Alert variant="destructive">
          <AlertDescription>{draft.error}</AlertDescription>
        </Alert>
      ) : null}

      {ready && text.length > 0 ? (
        <>
          <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-sm">{text}</p>

          {draft.citations.length > 0 ? (
            <div className="space-y-1">
              <p className="text-xs font-medium">{t('ai.draft_sources')}</p>
              <ul className="space-y-1">
                {draft.citations.map((citation) => (
                  <CitationItem key={`${citation.kind}-${citation.id}`} citation={citation} />
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{t('ai.draft_no_sources')}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={disabled} onClick={() => onTake(draft)}>
              {t('ai.draft_take')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled}
              onClick={() => onDiscard(draft)}
            >
              {t('ai.draft_discard')}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">{t('ai.draft_human_hint')}</p>
        </>
      ) : null}

      {!draft && !pending ? (
        <p className="text-sm text-muted-foreground">{t('ai.draft_empty')}</p>
      ) : null}
    </section>
  );
}
